/**
 * FIN-12351 (PRD §4.4) — grouping-key value redirection: all of the decision logic behind the
 * Perspective radio and the Swap toggle, with no React in it.
 *
 * Extracted for the same reason as `fieldOptions.js`: Functions.js cannot be imported in a test
 * (it drags in redux + the whole query-builder), and this is the part where a wrong answer is
 * SILENT — the backend accepts a mapping it cannot check (see "the three gates" below), so a bug
 * here reads the wrong aggregation store and reports a wrong number rather than an error.
 *
 * ── The contract ────────────────────────────────────────────────────────────────────────────
 * `aggregationArgs.groupingKeyToValueMapping` = `{ <grouping key column> : <event field to source
 * the VALUE from> }`. Omitted / empty = identity = legacy behaviour. Only the realtime READ key
 * moves; the grouping-key column set (and therefore the store) is unchanged. That is what lets a
 * `receiver_hashcode`-keyed store be read at the current transaction's SENDER value — "amounts
 * received by the current sender" — which is the primitive the D+C rule needs.
 *
 * ── The three gates ─────────────────────────────────────────────────────────────────────────
 *  G1  every mapping KEY must be one of that function's own `groupingKeys`, compared as an EXACT
 *      string (FraudAggregationFunction.scala validate). No canonicalisation on the BE side.
 *  G2  a non-empty mapping is rejected together with `includeCurrentTransaction = true`.
 *  G3  the (key, value) pair should come from `metadata_dropdown.grouping_key_pairs` — and this is
 *      NOT enforced anywhere on the backend. This module is the enforcement.
 *
 * ── Spelling ────────────────────────────────────────────────────────────────────────────────
 * Since FIN-13223 the drawer leaves a rule's STORED grouping keys in whatever spelling they were
 * saved with (legacy flat `sender_hashcode`, or canonical `transaction_event.sender_user_profile
 * .hashcode`), and only canonicalises for DISPLAY. The pair table is canonical-only. So:
 *   • look the pair row up by the CANONICAL id, and
 *   • write the mapping KEY back using the STORED spelling (G1 is an exact-string check), while
 *   • the mapping VALUE may be canonical — the BE read resolves it through FieldCatalog.byAnyName.
 * Getting this backwards rejects the save on every pre-FIN-12874 rule, which is everything in QA.
 */

/** Default rule event type / aggregation input type when a rule does not state one. */
const DEFAULT_INPUT_TYPE = 'transaction';

/**
 * The pair row for one aggregation function: `{ <canonical grouping key>: [value fields] }`.
 *
 * First axis: the event the rule is triggered on (the mapping VALUE is read off it). Second axis:
 * the class the function AGGREGATES — own-type for the ordinary case, the other class for a
 * cross-type function (FIN-13484: a transaction variant grouping activity history by `user_id`,
 * cell transaction/activity). Callers derive it with `feedingClassFor(applicableTo, triggeredOn)`;
 * never from `args.inputType`, which defaults to transaction, is never written by the drawer, and
 * does not reach shard identity (`GroupingKeyForAggregation(rule.inputType, agg.groupingKeys)`).
 */
export const pairsFor = (
  pairs,
  ruleEventType = DEFAULT_INPUT_TYPE,
  aggInputType = DEFAULT_INPUT_TYPE
) =>
  (pairs || {})[ruleEventType || DEFAULT_INPUT_TYPE]?.[
    aggInputType || DEFAULT_INPUT_TYPE
  ] || {};

/**
 * Canonical ⇄ stored-spelling resolver, built from the Group By option list (each entry carries
 * both `value` — the canonical id — and `legacyName`).
 */
export const buildSpellingResolver = (groupByArr = []) => {
  const canonicalByAnyName = new Map();
  const labelByCanonical = new Map();

  (groupByArr || []).forEach((option) => {
    if (!option || !option.value) return;
    canonicalByAnyName.set(option.value, option.value);
    if (option.legacyName) {
      canonicalByAnyName.set(option.legacyName, option.value);
    }
    if (option.label) labelByCanonical.set(option.value, option.label);
  });

  const toCanonical = (id) => canonicalByAnyName.get(id) || id;

  return {
    toCanonical,
    /**
     * The spelling a given canonical id is stored under in THIS rule's grouping keys — the string
     * a mapping key must use to satisfy G1. Falls back to canonical when the rule does not group
     * on it (which only happens for a mapping VALUE, where spelling does not matter).
     */
    toStored: (canonicalId, groupingKeys = []) =>
      (groupingKeys || []).find((k) => toCanonical(k) === canonicalId) ||
      canonicalId,
    /** Display label for a field id in either spelling. */
    labelOf: (id) => labelByCanonical.get(toCanonical(id)) || id
  };
};

/**
 * A grouping key is "paired" when the row offers it something other than itself.
 *
 * Tested BY VALUE, not by arity. `length > 1` assumed every cell lists the identity option
 * first, which is true of the same-type cells and false of the cross-type ones: those have NO
 * identity entry (FIN-13484 — the focal event cannot supply the other class's column, so an
 * unmapped read is never applicable; `defaultOptionOf` already returns `undefined` for them).
 * Under the arity test the reverse cell `activity/transaction`, whose keys each offer the single
 * value `user_id`, scored `length === 1` and was reported UNPAIRED — no Perspective control, so
 * the whole activity-rule-over-transaction-history direction was unreachable in the drawer.
 */
const isPaired = (canonicalKey, pairMap) =>
  (pairMap[canonicalKey] || []).some((value) => value !== canonicalKey);

/**
 * Mutual (swap-eligible): A lists B AND B lists A. Derived, never listed separately — that
 * two-entries-per-key doubling IS the swap mechanism, which is why one physical pair needs two
 * rows in the seed. A one-way entry is a perspective, not a swap.
 */
export const isMutual = (canonicalA, canonicalB, pairMap) =>
  (pairMap[canonicalA] || []).includes(canonicalB) &&
  (pairMap[canonicalB] || []).includes(canonicalA);

/**
 * Which control (if any) this Group By selection earns.
 *
 * `options` is the WHOLE list from the pair row (arity N, `[0]` = the identity default). Callers
 * must map over it — never index `[1]`, never branch on `length === 2`. Every seeded key happens
 * to carry exactly two entries today, but adding a third value source later is a DATA change and
 * must not need a code change.
 *
 * @returns {{kind:'perspective', key, canonicalKey, options}
 *          |{kind:'swap', keyA, keyB, canonicalA, canonicalB}
 *          |{kind:'none', reason}}
 */
export const controlFor = (
  groupingKeys = [],
  pairMap = {},
  resolver,
  { configPresent = false } = {}
) => {
  const keys = (groupingKeys || []).filter(Boolean);
  if (keys.length === 0) return { kind: 'none', reason: 'no-keys' };

  // An empty pair map has TWO very different causes and they must not share a message:
  //   - no `grouping_key_pairs` row reached the client at all → a deployment defect
  //     (seed migration unapplied, or a catalogue serving non-canonical ids)
  //   - the row is here but carries no branch for THIS rule's event type → documented scope;
  //     the seed populates transaction/transaction only, cross-type pairs are a follow-up.
  // Calling the second a deployment issue blames the environment for working as designed — the
  // mirror of the original bug, which blamed the field.
  if (Object.keys(pairMap || {}).length === 0) {
    return {
      kind: 'none',
      reason: configPresent ? 'not-configured-for-event' : 'no-pair-config'
    };
  }

  const paired = keys.filter((k) => isPaired(resolver.toCanonical(k), pairMap));
  if (paired.length === 0) return { kind: 'none', reason: 'unpaired' };

  // Mutual pairs first: a Group By of [sender, receiver, ip_address] earns the Swap toggle, with
  // ip_address left on identity — the extra key gets no control of its own.
  const mutualPairs = [];
  for (let i = 0; i < paired.length; i += 1) {
    for (let j = i + 1; j < paired.length; j += 1) {
      const a = resolver.toCanonical(paired[i]);
      const b = resolver.toCanonical(paired[j]);
      if (isMutual(a, b, pairMap)) {
        mutualPairs.push({
          keyA: paired[i],
          keyB: paired[j],
          canonicalA: a,
          canonicalB: b
        });
      }
    }
  }

  if (mutualPairs.length === 1) return { kind: 'swap', ...mutualPairs[0] };
  if (mutualPairs.length > 1) {
    return { kind: 'none', reason: 'multiple-mutual-pairs' };
  }

  if (paired.length === 1) {
    const canonicalKey = resolver.toCanonical(paired[0]);
    return {
      kind: 'perspective',
      key: paired[0],
      canonicalKey,
      options: pairMap[canonicalKey] || []
    };
  }

  // Two or more paired keys that are not a mutual pair (e.g. sender hashcode + sender msisdn).
  // Neither preset can express it; the mapping stays identity. Documented limitation (D2).
  return { kind: 'none', reason: 'multiple-non-mutual' };
};

/**
 * Perspective selection → mapping. Choosing the DEFAULT option returns `{}` so the caller DELETES
 * the key rather than storing an empty object or, worse, a redundant entry — such an entry is
 * accepted by G1/G2 but its mapping signature is non-empty, so the rule fans out as a MAPPED shard
 * distinct from the plain one. Same numbers, gratuitous second shard identity. Never emit it.
 *
 * The default is the IDENTITY option (the key listing itself), never a positional `options[0]`.
 * A cross-type cell (FIN-13484: transaction rule, activity `user_id` key, transaction value
 * fields) has NO identity — the triggering transaction carries no `user_id`, so an unmapped read
 * is never applicable and the BE flags it. There `defaultOptionOf` is `undefined`, nothing is
 * marked "(default)", an unset variant renders with no radio selected, and choosing ANY option
 * writes the mapping.
 */
/**
 * The option that means "no redirection" — what "(default)" marks, what an empty mapping selects,
 * and what selecting writes `{}` for. Found BY VALUE, not by position: a pair row seeded out of
 * order (`{receiver: [sender, receiver]}`) would otherwise put the marker on a redirect and make
 * the default choice write a mapping. `undefined` when the cell has no identity option (cross-type).
 */
export const defaultOptionOf = (control, resolver) => {
  const options = control?.options || [];
  const canonicalKey = control?.canonicalKey;
  if (canonicalKey === undefined) return undefined;
  // Same spelling trap as presetFromMap: an identity entry written in the key's OTHER vocabulary
  // is still the identity. Fall back to a plain match when no resolver is supplied.
  const toCanonical = resolver ? resolver.toCanonical : (id) => id;
  return options.find((option) => toCanonical(option) === toCanonical(canonicalKey));
};

export const mapFromPerspective = (storedKey, defaultValue, canonicalValue) =>
  !canonicalValue || canonicalValue === defaultValue
    ? {}
    : { [storedKey]: canonicalValue };

/**
 * FIN-13548: PER-KEY editing, for the selections no preset can express.
 *
 * `controlFor` offers the Perspective radio for exactly one paired key and the Swap toggle for one
 * sender/receiver pair. Anything else — two or more paired keys that are not a mutual pair — got
 * `{kind: 'none'}` and therefore NO control at all, so its mapping could never be set in the
 * drawer. Harmless while the checks were advisory; under native-only (FIN-13484) such a rule
 * cannot match, and since FIN-13538 it also blocks the threshold-generation launch.
 *
 * One row per grouping key, each offering what its OWN pair row offers. Returns a row even when a
 * key has nothing to offer, so the table can say why instead of rendering a silent identity.
 *
 * @returns {{storedKey, canonicalKey, options: string[], identity: string|undefined,
 *            reason: 'ok'|'no-pair-row'|'self-only'}[]}
 */
export const perKeyControls = (
  storedGroupingKeys = [],
  pairMap = {},
  resolver
) => {
  const toCanonical = resolver ? resolver.toCanonical : (id) => id;
  return (storedGroupingKeys || []).filter(Boolean).map((storedKey) => {
    const canonicalKey = toCanonical(storedKey);
    const options = (pairMap || {})[canonicalKey] || [];
    // Identity is the option that means "read this key at its own value" — matched BY VALUE and
    // through the resolver, since a row may spell it in the key's other vocabulary.
    const identity = options.find(
      (option) => toCanonical(option) === canonicalKey
    );
    let reason = 'ok';
    if (options.length === 0) reason = 'no-pair-row';
    else if (options.every((option) => toCanonical(option) === canonicalKey)) {
      reason = 'self-only';
    }
    return { storedKey, canonicalKey, options, identity, reason };
  });
};

/**
 * FIN-13548: write ONE key's value into an existing mapping, leaving the others alone.
 *
 * The per-key analogue of `mapFromPerspective`, and it must preserve the rest of the map: the
 * radio owns the whole mapping because it governs a single key, whereas a per-key edit is one
 * entry of several. Choosing the identity (or clearing) DELETES that entry rather than writing a
 * redundant self-reference, matching `mapFromPerspective`'s contract and keeping an untouched
 * legacy rule byte-identical on save.
 */
export const mapWithKeyValue = (
  mapping = {},
  storedKey,
  identity,
  canonicalValue
) => {
  const next = { ...(mapping || {}) };
  if (!canonicalValue || canonicalValue === identity) {
    delete next[storedKey];
  } else {
    next[storedKey] = canonicalValue;
  }
  return next;
};

/** Swap toggle → mapping. Off returns `{}` so the caller deletes the key. */
export const mapFromSwap = (
  storedKeyA,
  storedKeyB,
  canonicalA,
  canonicalB,
  on
) => (on ? { [storedKeyA]: canonicalB, [storedKeyB]: canonicalA } : {});

/**
 * Mapping → the preset that produced it, so a stored rule (including one authored in AFC or by
 * hand) renders with the right control selected instead of being normalised away.
 *
 * `custom` means the map is real but no preset explains it — render the read-only row and the
 * sentence, hide the control, and leave the map untouched on save.
 *
 * @returns {{kind:'perspective', selected}|{kind:'swap', on}|{kind:'none'}|{kind:'custom'}}
 */
export const presetFromMap = (control, mapping = {}, resolver) => {
  const map = mapping || {};
  const entries = Object.keys(map);

  if (control.kind === 'perspective') {
    const value = map[control.key];
    if (entries.length === 0) {
      // The identity option within one event type; `undefined` (no radio checked) on a
      // cross-type cell, where an unmapped rule is inert and must not look configured.
      return { kind: 'perspective', selected: defaultOptionOf(control, resolver) };
    }
    if (entries.length === 1 && value !== undefined) {
      // Compare CANONICAL to CANONICAL, then return the OPTION's own spelling — the radio's value
      // is the option string, so handing back the canonical id would check nothing. The stored
      // value and the pair row can legitimately differ in spelling on an environment whose
      // `custom_field.api_path` is only partly backfilled: the row is mirrored to its key's
      // vocabulary while the resolver canonicalises to the catalogue's. Comparing a canonicalised
      // value against raw options reported `custom` for a perfectly good mapping, which rendered
      // as "select a Perspective — the rule can never match".
      const selected = resolver.toCanonical(value);
      const match = (control.options || []).find(
        (option) => resolver.toCanonical(option) === selected
      );
      if (match !== undefined) {
        return { kind: 'perspective', selected: match };
      }
    }
    return { kind: 'custom' };
  }

  if (control.kind === 'swap') {
    if (entries.length === 0) return { kind: 'swap', on: false };
    const a = map[control.keyA];
    const b = map[control.keyB];
    // Deliberately NOT `entries.length === 2`: a map carrying the crossed pair PLUS a third
    // entry is still a live swap, and reporting `custom` rendered the toggle OFF while the swap
    // was in force — an unchecked toggle actively misstates the rule. The extra entry is surfaced
    // by the stranded-key warning instead, which is where it belongs.
    if (
      a !== undefined &&
      b !== undefined &&
      resolver.toCanonical(a) === control.canonicalB &&
      resolver.toCanonical(b) === control.canonicalA
    ) {
      return { kind: 'swap', on: true };
    }
    return { kind: 'custom' };
  }

  return entries.length === 0 ? { kind: 'none' } : { kind: 'custom' };
};

/**
 * Drop entries whose key is no longer a grouping key.
 *
 * This is a correctness guard, not tidiness: deselecting a grouping key while its mapping entry
 * survives fails G1 on save, from a UI action that looks unrelated to the mapping.
 */
export const pruneMapping = (mapping = {}, groupingKeys = [], resolver) => {
  const entries = Object.keys(mapping || {});
  if (entries.length === 0) return {};

  // Re-KEY rather than drop. The Group By control renders canonical option ids against
  // `args.groupingKeys.map(toCanonicalId)`, so antd's onChange emits canonical ids for EVERY
  // selected key — including ones the user never touched. On a rule saved with the legacy
  // spelling, an exact-string prune therefore deleted a still-valid mapping the moment any
  // Group By edit happened, silently changing the rule's read semantics. Matching on the
  // canonical identity and re-keying to the surviving spelling keeps the redirect intact and
  // keeps the key in step with G1 (which compares the mapping key to `groupingKeys` exactly).
  const toCanonical = resolver ? resolver.toCanonical : (x) => x;
  const survivorFor = (mappingKey) =>
    (groupingKeys || []).find((gk) => toCanonical(gk) === toCanonical(mappingKey));

  return entries.reduce((acc, key) => {
    const survivor = survivorFor(key);
    if (survivor !== undefined) acc[survivor] = mapping[key];
    return acc;
  }, {});
};

/**
 * Next `args` after a Group By change: the new keys, with the mapping re-keyed to match.
 *
 * Extracted so the reconciliation is unit-testable. It used to live inline in
 * `Functions.onChangeSelectType`, where nothing reached it — `pruneMapping` was tested in
 * isolation while the caller, which is where the canonical-id shift actually happens, had no
 * coverage at all. That is precisely the seam the legacy-spelling bug slipped through.
 *
 * Returns args with `groupingKeyToValueMapping` ABSENT when nothing survives, so an untouched
 * legacy rule stays byte-identical in the saved payload.
 */
export const reconcileGroupingKeys = (args = {}, nextGroupingKeys = [], resolver) => {
  const next = { ...args, groupingKeys: nextGroupingKeys };
  const mapping = pruneMapping(
    args.groupingKeyToValueMapping,
    nextGroupingKeys,
    resolver
  );
  if (Object.keys(mapping).length === 0) delete next.groupingKeyToValueMapping;
  else next.groupingKeyToValueMapping = mapping;
  return next;
};

/**
 * A non-empty mapping alongside a grouping key that is neither mapped nor pairable is almost
 * certainly wrong: every unpaired grouping key reachable from a transaction rule is actor- or
 * instrument-scoped (ip_address, device.*, session.*, card_hash, wallet_id, loan ids), so it keeps
 * describing the ORIGINAL party while the identity column is redirected to the counterparty. The
 * read then asks for the counterparty's rows at the current party's IP and matches nothing.
 *
 * Advisory only — the backend accepts it, and the fully-mapped Swap case is legitimate.
 */
export const strandedGroupingKeys = (
  groupingKeys = [],
  mapping = {},
  pairMap = {},
  resolver
) => {
  const map = mapping || {};
  if (Object.keys(map).length === 0) return [];
  const toCanonical = resolver ? resolver.toCanonical : (x) => x;
  const mapped = new Set(Object.keys(map).map(toCanonical));
  // The condition is "not redirected", NOT "unpaired". Keying off pairedness missed the case the
  // Swap toggle actually produces: Group By [sender.hashcode, receiver.hashcode, sender.msisdn]
  // earns Swap on the one mutual pair, the two hashcodes flip, and sender.msisdn rides along on
  // identity — paired, so the old guard ignored it, while the read asks for the receiver's rows
  // at the current SENDER's msisdn and matches nothing. Pairedness now only softens the wording.
  return (groupingKeys || []).filter((k) => !mapped.has(toCanonical(k)));
};

/** True when a stranded key has a pair of its own — used only to word the warning. */
export const isPairedKey = (key, pairMap = {}, resolver) =>
  isPaired(resolver ? resolver.toCanonical(key) : key, pairMap);

// ── "Reads as" ────────────────────────────────────────────────────────────────────────────────
// The analyst's check that the mapping means what they think. The raw key → field row is the
// developer's check; this sentence is the one a reviewer can argue with.
//
// DIRECTION comes from the grouping key (which store is read); the PARTY comes from the mapped
// value (whose value forms the read key). Both spellings are matched, canonical
// (`transaction_event.sender_user_profile.hashcode`, `transaction_event.sender_partner.code`) and
// legacy (`sender_hashcode`, `sending_partner_code`).

const SENDER_RE = /(^|[._])(sender|sending)([._]|$)/;
const RECEIVER_RE = /(^|[._])(receiver|receiving)([._]|$)/;

export const partyOf = (fieldId) => {
  if (!fieldId) return null;
  if (SENDER_RE.test(fieldId)) return 'sender';
  if (RECEIVER_RE.test(fieldId)) return 'receiver';
  return null;
};

const DIRECTION_WORD = { sender: 'sent', receiver: 'received' };

/**
 * "the current transaction's sender" — but only when the field IS the party identity.
 *
 * All five seeded pairs are sender/receiver mirrors, so naming the party alone collapsed every one
 * of them to the same sentence: grouping on the sender's hashcode, advance hashcode, identifier
 * hashcode, msisdn and the SENDING PARTNER CODE all read "...by the current transaction's sender".
 * Those are materially different aggregations, and the sentence is meant to be the thing a reviewer
 * can argue with, so anything that is not the plain party identity names its own field.
 */
// Last-resort label for the SENTENCE only. `labelOf` resolves from the Group By options, so a
// field the current event type does not offer — e.g. a transaction grouping key left on a variant
// whose Triggered On was switched to Activity — falls through to the raw storage column. A column
// name is precisely the jargon this sentence exists to replace.
//
// Deliberately NOT applied to the mapping ROW above it: that row is the developer's check and must
// keep showing the value exactly as stored.
const humanise = (id) =>
  String(id)
    .split('.')
    .pop()
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const sentenceLabel = (id, resolver) => {
  const label = resolver.labelOf(id);
  return label === id ? humanise(id) : label;
};

// The field that IS the party, as opposed to one of its attributes. Only for such a field can the
// sentence say "the current transaction's sender" and stop; anything else has to name itself, or
// several different pairs read identically.
//
// Matched on the last SEGMENT, not on a suffix. `/(^|[._])hashcode$/` also matched
// `sender_advance_hashcode` and `sender_identifier_hashcode` — the `_` before `hashcode` satisfies
// the separator class — so all three sender pairs rendered "the current transaction's sender", and a
// rule grouped by the advance hashcode was indistinguishable from one grouped by the plain one.
// Canonical ids separate segments with `.`; the legacy flat spellings are a closed set of two.
const isPartyIdentity = (fieldId) =>
  /\.hashcode$/.test(fieldId) || /^(sender|receiver)_hashcode$/.test(fieldId);

export const partyPhraseFor = (fieldId, resolver, eventNoun = 'transaction') => {
  const label = sentenceLabel(fieldId, resolver);
  // "sender" and "receiver" describe the two sides of a TRANSACTION. An activity event has one
  // actor and no counterparty, so on an activity-triggered variant the party word would describe
  // an event that is not there — name the field instead.
  if (eventNoun !== 'transaction') return `the current ${eventNoun}'s ${label}`;

  const party = partyOf(fieldId);
  if (!party) return `the current transaction's ${label}`;
  if (isPartyIdentity(fieldId)) return `the current transaction's ${party}`;
  // e.g. "the current transaction's sender (Sender Phone Number)"
  return `the current transaction's ${party} (${label})`;
};

/**
 * FIN-13860: the one-line warning under "Reads as" for a redirected read over activity events.
 *
 * A Perspective never regroups. `user_id -> beneficiary_party_id` counts the activity OF the
 * beneficiary (as an actor), not the activity of every customer who names that beneficiary; the
 * FP1981 data had beneficiaries that were never actors, so the variants could not fire (FIN-13677,
 * FIN-13860). Say so, and point at the configuration that does aggregate across customers — a
 * native Group By on the value field — only when this function actually offers it.
 *
 * Transaction-scope redirects (incoming vs outgoing, FIN-12351) read exactly as intended and get
 * no hint.
 *
 * @returns {string|null}
 */
export const perspectiveHint = ({
  groupingKeys = [],
  mapping = {},
  overActivity = false,
  groupByOptions = [],
  resolver
}) => {
  if (!overActivity) return null;
  const map = mapping || {};
  const redirected = (groupingKeys || []).find(
    (k) => map[k] !== undefined && map[k] !== k
  );
  if (!redirected) return null;

  const valueField = map[redirected];
  const whose = isPartyIdentity(valueField)
    ? partyOf(valueField)
    : sentenceLabel(valueField, resolver);
  const offered = new Set();
  (groupByOptions || []).forEach((o) => {
    if (o && o.value) offered.add(o.value);
    if (o && o.legacyName) offered.add(o.legacyName);
  });
  const own = `This counts only the ${whose}'s own activity, not the activity of every customer that shares the same ${whose}.`;
  return offered.has(valueField)
    ? `${own} To count across customers, set Group By to ${sentenceLabel(
        valueField,
        resolver
      )} and leave Perspective at its default.`
    : `${own} Counting across customers by ${whose} is not available for this function.`;
};

/**
 * @param {object} p
 * @param {string} p.aggFunctionLabel  e.g. "Sum"
 * @param {string} p.fieldLabel        e.g. "Transaction Amount"
 * @param {string[]} p.groupingKeys    stored spellings
 * @param {object} p.mapping           groupingKeyToValueMapping
 * @param {boolean} p.overActivity     applicableTo includes 'activity' (cross-type)
 * @param {object} p.resolver          from buildSpellingResolver
 */
export const readsAsSentence = ({
  aggFunctionLabel = 'Aggregation',
  fieldLabel = '',
  groupingKeys = [],
  mapping = {},
  overActivity = false,
  // The event that TRIGGERS the rule. Distinct from `overActivity`, which is about what gets
  // aggregated (applicableTo) — a transaction rule can aggregate activity events.
  eventNoun = 'transaction',
  resolver
}) => {
  const keys = (groupingKeys || []).filter(Boolean);
  const map = mapping || {};

  // The direction qualifies the EVENTS being aggregated, never the field. Catalogue labels already
  // carry party and direction of their own ("Amount sent by the Sender in USD"), so folding the
  // direction onto the field produced "Sum of Amount sent by the Sender in USD sent by the current
  // transaction's sender" — and, for a swap, read as though the amount were the thing flowing.
  // Hanging the direction off a scope noun ("over transactions ...") separates the two whatever the
  // catalogue calls the field.
  const subject = fieldLabel
    ? `${aggFunctionLabel} of ${fieldLabel}`
    : aggFunctionLabel;
  const scope = overActivity ? 'activity events' : 'transactions';

  if (keys.length === 0) return `${subject}.`;

  // Swap: two keys, each sourcing the other's value.
  if (keys.length >= 2) {
    const swapped = keys.filter(
      (k) => map[k] !== undefined && partyOf(k) && partyOf(map[k])
    );
    if (
      swapped.length === 2 &&
      partyOf(swapped[0]) !== partyOf(swapped[1]) &&
      partyOf(map[swapped[0]]) === partyOf(swapped[1]) &&
      partyOf(map[swapped[1]]) === partyOf(swapped[0])
    ) {
      return `${subject} over ${scope} flowing the opposite way on this pair (receiver \u2192 sender).`;
    }
  }

  // Otherwise describe the one key that carries a direction, preferring a mapped one.
  const directional =
    keys.find((k) => map[k] !== undefined && partyOf(k)) ||
    keys.find((k) => partyOf(k));

  if (!directional) {
    // No key carries a sender/receiver party — but a redirect may still be in force. Activity
    // pairs (FIN-13431) are actor -> beneficiary / signatory, and NEITHER side has a party word,
    // so without this the sentence fell through to "grouped by <the key>" and silently omitted the
    // redirect: it would say the aggregation is grouped by the actor while the value is actually
    // taken from the beneficiary. Describing the wrong field is worse than saying nothing.
    const redirected = keys.find((k) => map[k] !== undefined);
    if (redirected) {
      const rest = keys
        .filter((k) => k !== redirected)
        .map((k) => sentenceLabel(map[k] || k, resolver));
      const tail = rest.length ? `, for the same ${rest.join(' and ')}` : '';
      // FIN-13860: a Perspective is a READ redirect — the bucket stays keyed by the grouping key
      // (the actor), the mapped value only picks WHICH actor's bucket is read. "activity events of
      // the current activity's Beneficiary Party Id" read as "events sharing this beneficiary"
      // (a regroup), which is what QA configured it for twice (FIN-13677, FIN-13860). Name the
      // actor as the one who PERFORMED the events, and — when the value is an attribute rather than
      // a party identity — say whose attribute it is matched against.
      const valueField = map[redirected];
      const phrase = partyPhraseFor(valueField, resolver, eventNoun);
      const actor = isPartyIdentity(valueField)
        ? phrase
        : `the party whose ${sentenceLabel(redirected, resolver)} equals ${phrase}`;
      return `${subject} over ${scope} performed by ${actor}${tail}.`;
    }
    const labels = keys.map((k) => sentenceLabel(k, resolver)).join(' and ');
    return `${subject} over ${scope} grouped by the current ${eventNoun}'s ${labels}.`;
  }

  const valueField = map[directional] || directional;
  const partyPhrase = partyPhraseFor(valueField, resolver, eventNoun);

  const others = keys
    .filter((k) => k !== directional)
    .map((k) => sentenceLabel(map[k] || k, resolver));
  const suffix = others.length ? `, for the same ${others.join(' and ')}` : '';

  // An activity event has no direction of its own — it belongs to a party, so the sent/received
  // word would be a lie there. Only transaction scope carries it.
  if (overActivity) {
    return `${subject} over ${scope} performed by ${partyPhrase}${suffix}.`;
  }

  const direction = DIRECTION_WORD[partyOf(directional)];
  return `${subject} over ${scope} ${direction} by ${partyPhrase}${suffix}.`;
};

/**
 * The advisory shown when `unmappedUnpairedKeys` is non-empty. Lives here, next to the predicate,
 * so the wording is unit-tested rather than buried in JSX.
 */
export const strandedKeysWarning = (keys = [], eventNoun = 'transaction') => {
  const labels = keys.map((k) => k.label);
  const list = labels.join(', ');
  const verb = labels.length > 1 ? 'are' : 'is';
  const anyPaired = keys.some((k) => k.paired);
  // A stranded key that has a pair of its own reads differently to one that never could: the first
  // is an omission the analyst can fix by grouping differently, the second is inherent to the field.
  const tail = anyPaired
    ? 'Redirect it too, group by the party identity alone, or return the control to its default.'
    : 'Group by the party identity alone, or return the control to its default.';
  return (
    `${list} ${verb} not redirected, so ${labels.length > 1 ? 'they stay' : 'it stays'} on the ` +
    `current ${eventNoun}'s own value while the redirected key reads the counterparty's. This ` +
    `aggregation looks for the counterparty's rows at the current party's ${list} and will ` +
    `usually match nothing. ${tail}`
  );
};
